// src/components/motion/primitives.tsx
// framer-motion ベースの共有モーション部品。全て prefers-reduced-motion を尊重する。
import React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { motion as motionTokens } from '@/theme/tokens';

const EASE = motionTokens.ease;

/** 下からふわっと現れる。リストの子にも使える。 */
export const fadeUpVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: motionTokens.duration.base, ease: EASE },
    },
};

/** 子要素を順番に表示するコンテナ。 */
export const staggerContainer: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};

type DivProps = React.ComponentProps<typeof motion.div>;

/** 表示時に下からフェードイン。reduced-motion時は即表示。 */
export const FadeInUp: React.FC<DivProps & { delay?: number }> = ({
    children,
    delay = 0,
    ...rest
}) => {
    const reduce = useReducedMotion();
    if (reduce) return <div {...(rest as any)}>{children}</div>;
    return (
        <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUpVariants}
            transition={{ delay }}
            {...rest}
        >
            {children}
        </motion.div>
    );
};

/** 子に fadeUpVariants を持たせると順次表示されるコンテナ。 */
export const StaggerGroup: React.FC<DivProps> = ({ children, ...rest }) => {
    const reduce = useReducedMotion();
    if (reduce) return <div {...(rest as any)}>{children}</div>;
    return (
        <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-40px' }}
            variants={staggerContainer}
            {...rest}
        >
            {children}
        </motion.div>
    );
};

/** Stagger の子。中身を fadeUp で出す。 */
export const StaggerItem: React.FC<DivProps> = ({ children, ...rest }) => {
    const reduce = useReducedMotion();
    if (reduce) return <div {...(rest as any)}>{children}</div>;
    return (
        <motion.div variants={fadeUpVariants} {...rest}>
            {children}
        </motion.div>
    );
};

/** 押すと少し沈む、上質なタップ感。ボタン/カードのラッパ。 */
export const Pressable: React.FC<DivProps> = ({ children, ...rest }) => {
    const reduce = useReducedMotion();
    if (reduce) return <div {...(rest as any)}>{children}</div>;
    return (
        <motion.div
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: motionTokens.duration.fast, ease: EASE }}
            {...rest}
        >
            {children}
        </motion.div>
    );
};
